import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Users, FileText, Target, Zap } from "lucide-react";

const categories = [
  { value: "Safety", label: "Patient Safety/Risk", icon: Shield, color: "text-red-400" },
  { value: "Staff", label: "Staff/Culture", icon: Users, color: "text-blue-400" },
  { value: "Admin", label: "Admin/Regulatory", icon: FileText, color: "text-amber-400" },
  { value: "Strategic", label: "Strategic/Improvement", icon: Target, color: "text-emerald-400" },
];

const priorities = [
  { value: "Today", label: "Today", color: "badge-today" },
  { value: "ThisWeek", label: "This Week", color: "badge-thisweek" },
  { value: "ParkingLot", label: "Parking Lot", color: "badge-parking" },
];

export const QuickCapture = ({ open, onClose }) => {
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Safety");
  const [priority, setPriority] = useState("Today");
  const [nextStep, setNextStep] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      toast.error("Please enter a task description");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/tasks`, {
        description: description.trim().slice(0, 100),
        category,
        priority,
        next_step: nextStep.trim() || null,
      }, { withCredentials: true });

      toast.success("Task captured!", {
        description: `Added to ${priority === 'Today' ? "Today" : priority === 'ThisWeek' ? "This Week" : "Parking Lot"}`,
      });

      // Reset form
      setDescription("");
      setCategory("Safety");
      setPriority("Today");
      setNextStep("");
      onClose();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to capture task");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md glass border-violet-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-violet-400" />
            </div>
            Quick Capture
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Task Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Task (max 100 chars)</Label>
            <Input
              id="description"
              data-testid="quick-capture-description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 100))}
              placeholder="What needs to be done?"
              className="text-base"
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/100
            </p>
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  data-testid={`category-${cat.value.toLowerCase()}`}
                  onClick={() => setCategory(cat.value)}
                  className={`flex items-center gap-2 p-2 rounded-lg border text-sm transition-colors
                    ${category === cat.value 
                      ? 'border-primary bg-primary/10' 
                      : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                >
                  <cat.icon className={`w-4 h-4 ${cat.color}`} />
                  {cat.label.split('/')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Priority Selection */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    <span className={`px-2 py-0.5 rounded text-xs ${p.color}`}>
                      {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Next Step (for ADHD restart-ability) */}
          <div className="space-y-2">
            <Label htmlFor="nextStep">Next Step (optional)</Label>
            <Input
              id="nextStep"
              data-testid="quick-capture-next-step"
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              placeholder="What's the very first action?"
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Helps you restart if interrupted
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-2">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="capture-btn"
              disabled={isSubmitting}
              data-testid="quick-capture-submit"
            >
              {isSubmitting ? "Capturing..." : "Capture"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
